/**
 * Guide Page
 * Displays guide/documentation content fetched from API
 */

import { Link, useParams } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import { MarkdownRenderer } from "@components/exercise";
import { useGuides, useGuide } from "@hooks/useGuides";
import type { GuideIcon } from "@/types/models";

// Types for Table of Contents
interface TocItem {
  id: string;
  text: string;
  level: number;
}

/**
 * Extracts headings from HTML content for Table of Contents
 */
function extractHeadings(html: string): TocItem[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const headings = doc.querySelectorAll("h1, h2, h3");
  const items: TocItem[] = [];

  headings.forEach((heading, index) => {
    const level = parseInt(heading.tagName[1]);
    const text = heading.textContent?.trim() || "";
    // Generate a safe ID from the heading text
    const id =
      heading.id ||
      `heading-${index}-${text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")}`;

    if (text) {
      items.push({ id, text, level });
    }
  });

  return items;
}

/**
 * Adds IDs to headings in the rendered content
 */
function addHeadingIds(containerRef: HTMLElement | null, headings: TocItem[]) {
  if (!containerRef) return;

  const domHeadings = containerRef.querySelectorAll("h1, h2, h3");
  domHeadings.forEach((heading, index) => {
    if (headings[index]) {
      heading.id = headings[index].id;
    }
  });
}

/**
 * Table of Contents Component
 */
function TableOfContents({
  headings,
  activeId,
  onHeadingClick,
}: {
  headings: TocItem[];
  activeId: string;
  onHeadingClick: (id: string) => void;
}) {
  if (headings.length === 0) return null;

  return (
    <nav className="space-y-1">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
        On this page
      </h4>
      {headings.map((heading) => (
        <button
          key={heading.id}
          onClick={() => onHeadingClick(heading.id)}
          className={`block w-full text-left text-sm py-1.5 transition-colors duration-150
            ${heading.level === 1 ? "pl-0" : heading.level === 2 ? "pl-0" : "pl-4"}
            ${
              activeId === heading.id
                ? "text-primary-600 dark:text-primary-400 font-medium"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            }`}
        >
          {heading.text}
        </button>
      ))}
    </nav>
  );
}

/**
 * Mobile Table of Contents Dropdown
 */
function MobileToc({
  headings,
  activeId,
  onHeadingClick,
}: {
  headings: TocItem[];
  activeId: string;
  onHeadingClick: (id: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const activeHeading = headings.find((h) => h.id === activeId);

  if (headings.length === 0) return null;

  return (
    <div className="lg:hidden mb-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
      >
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {activeHeading?.text || "Table of Contents"}
        </span>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="mt-2 py-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg">
          {headings.map((heading) => (
            <button
              key={heading.id}
              onClick={() => {
                onHeadingClick(heading.id);
                setIsOpen(false);
              }}
              className={`block w-full text-left text-sm px-4 py-2 transition-colors
                ${heading.level === 3 ? "pl-8" : "pl-4"}
                ${
                  activeId === heading.id
                    ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
            >
              {heading.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Simple loading spinner component
function LoadingSpinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
    </div>
  );
}

// Icon components for guide cards
function BookIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}

function RocketIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"
      />
    </svg>
  );
}

function TerminalIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z"
      />
    </svg>
  );
}

function WrenchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M21.75 6.75a4.5 4.5 0 01-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 11-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 016.336-4.486l-3.276 3.276a3.004 3.004 0 002.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852z"
      />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z"
      />
    </svg>
  );
}

const iconMap: Record<GuideIcon, React.FC<{ className?: string }>> = {
  book: BookIcon,
  rocket: RocketIcon,
  terminal: TerminalIcon,
  wrench: WrenchIcon,
  play: PlayIcon,
};

export function GuidesListPage() {
  const { data: guides = [], isLoading, error } = useGuides();

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800 dark:text-red-200">
            Error Loading Guides
          </h2>
          <p className="mt-2 text-red-600 dark:text-red-300">
            Failed to load guides. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Guides & Documentation
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Learn how to use the ADK platform with these step-by-step guides.
        </p>
      </div>

      {/* Guides Grid */}
      <div className="grid gap-4 sm:grid-cols-2">
        {guides.map((guide) => {
          const IconComponent = iconMap[guide.icon] || BookIcon;
          return (
            <Link
              key={guide.slug}
              to={`/guides/${guide.slug}`}
              className="group block p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-50 dark:bg-primary-900/30 rounded-lg flex items-center justify-center group-hover:bg-primary-100 dark:group-hover:bg-primary-900/50 transition-colors">
                  <IconComponent className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {guide.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {guide.description}
                  </p>
                </div>
                <svg
                  className="flex-shrink-0 w-5 h-5 text-gray-400 group-hover:text-primary-500 transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </Link>
          );
        })}
      </div>

      {guides.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">
            No guides available yet.
          </p>
        </div>
      )}
    </div>
  );
}

export function GuidePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: guide, isLoading, error } = useGuide(slug);
  const [activeHeadingId, setActiveHeadingId] = useState<string>("");
  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null);

  // Extract headings from guide content
  const contentHtml = guide?.contentHtml;
  const headings = useMemo(() => {
    if (!contentHtml) return [];
    return extractHeadings(contentHtml);
  }, [contentHtml]);

  // Add IDs to headings in the DOM after render
  useEffect(() => {
    if (contentRef && headings.length > 0) {
      addHeadingIds(contentRef, headings);
    }
  }, [contentRef, headings]);

  // Set up Intersection Observer for scroll-based highlighting
  useEffect(() => {
    if (!contentRef || headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the first visible heading
        const visibleEntry = entries.find((entry) => entry.isIntersecting);
        if (visibleEntry) {
          setActiveHeadingId(visibleEntry.target.id);
        }
      },
      {
        rootMargin: "-80px 0px -70% 0px",
        threshold: 0,
      },
    );

    // Observe all headings
    const domHeadings = contentRef.querySelectorAll("h1, h2, h3");
    domHeadings.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, [contentRef, headings]);

  // Handle TOC click - smooth scroll to heading
  const handleHeadingClick = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveHeadingId(id);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !guide) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Guide Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The guide you're looking for doesn't exist.
          </p>
          <Link
            to="/guides"
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            Return to Guides
          </Link>
        </div>
      </div>
    );
  }

  const hasToc = headings.length > 1;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
        <Link
          to="/"
          className="hover:text-primary-600 dark:hover:text-primary-400"
        >
          Dashboard
        </Link>
        <span>/</span>
        <Link
          to="/guides"
          className="hover:text-primary-600 dark:hover:text-primary-400"
        >
          Guides
        </Link>
        <span>/</span>
        <span className="text-gray-900 dark:text-white">{guide.title}</span>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {guide.title}
        </h1>
        {guide.description && (
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            {guide.description}
          </p>
        )}
      </div>

      {/* Mobile TOC */}
      {hasToc && (
        <MobileToc
          headings={headings}
          activeId={activeHeadingId}
          onHeadingClick={handleHeadingClick}
        />
      )}

      {/* Main content area with optional sidebar */}
      <div
        className={`${hasToc ? "lg:grid lg:grid-cols-[1fr_220px] lg:gap-8" : ""}`}
      >
        {/* Content */}
        <div
          ref={setContentRef}
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8"
        >
          <MarkdownRenderer content={guide.contentHtml} />
        </div>

        {/* Desktop TOC Sidebar */}
        {hasToc && (
          <aside className="hidden lg:block">
            <div className="sticky top-24 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <TableOfContents
                headings={headings}
                activeId={activeHeadingId}
                onHeadingClick={handleHeadingClick}
              />
            </div>
          </aside>
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 flex items-center justify-between">
        <Link
          to="/guides"
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-gray-800 rounded-lg transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Guides
        </Link>
      </div>
    </div>
  );
}
